/**
 * VerifyEmailPage — handles `/verify?token=...` from the email link.
 *
 * Reads the token from the URL on mount, hits `/auth/verify`, then shows
 * one of three states: pending / success / failure (expired or already
 * used). Uses the `services` barrel so it stays in lockstep with the
 * rest of the auth flow.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { authService } from "@/services";
import { useAuth } from "@/contexts/AuthContext";

export default function VerifyEmailPage() {
  const { t } = useTranslation();
  usePageTitle("Verify email");
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [state, setState] = useState(token ? "loading" : "missing");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    authService
      .verifyEmail(token)
      .then(async () => {
        if (cancelled) return;
        setState("success");
        // Refresh /auth/me so the cached `email_verified=false` in
        // AuthContext flips. Otherwise the unverified banner on the
        // dashboard sticks until the user manually reloads.
        try { await checkAuth(); } catch { /* noop */ }
      })
      .catch(() => {
        if (!cancelled) setState("failed");
      });
    return () => {
      cancelled = true;
    };
  }, [token, checkAuth]);

  return (
    <div className="min-h-screen bg-[#E3F2FD] dark:bg-[#0a1428] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-lg p-8 text-center">
        {state === "loading" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-[#38B6FF] animate-spin" />
            <h1 className="mt-4 text-xl font-bold text-[#1A237E] dark:text-[#E3F2FD]">{t("verifyEmail.loading")}</h1>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
            <h1 className="mt-4 text-xl font-bold text-[#1A237E] dark:text-[#E3F2FD]">{t("verifyEmail.successTitle")}</h1>
            <p className="mt-2 text-sm text-[#1A237E]/70 dark:text-[#E3F2FD]/70">{t("verifyEmail.successDescription")}</p>
            <Button
              data-testid="verify-continue-btn"
              className="mt-6 w-full h-11 rounded-full bg-[#38B6FF] hover:bg-[#1A237E] text-white font-bold"
              onClick={() => navigate("/dashboard")}
            >
              {t("verifyEmail.button.continue")}
            </Button>
          </>
        )}
        {(state === "failed" || state === "missing") && (
          <>
            <AlertCircle className="w-12 h-12 mx-auto text-red-500" />
            <h1 className="mt-4 text-xl font-bold text-[#1A237E] dark:text-[#E3F2FD]">
              {state === "missing" ? t("verifyEmail.missingTitle") : t("verifyEmail.failedTitle")}
            </h1>
            <p className="mt-2 text-sm text-[#1A237E]/70 dark:text-[#E3F2FD]/70">
              {state === "missing"
                ? t("verifyEmail.missingDescription")
                : t("verifyEmail.failedDescription")}
            </p>
            <Link to="/login" className="block mt-6">
              <Button
                data-testid="verify-back-to-login"
                className="w-full h-11 rounded-full bg-[#38B6FF] hover:bg-[#1A237E] text-white font-bold"
              >
                {t("verifyEmail.button.backToLogin")}
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
